/**
 * Handles double-sided arrow drag end operation
 * @param {string} id - Double-sided arrow ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} doubleSidedArrows - Current double-sided arrows array
 * @param {Function} setDoubleSidedArrows - Function to set double-sided arrows
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentArrow - Optional: pass current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleDoubleSidedArrowDragEnd = (
  id,
  x,
  y,
  doubleSidedArrows,
  setDoubleSidedArrows,
  saveToHistory,
  isUndoingRedoing,
  currentArrow = null // Optional: pass current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
) => {
  // Safety check: ensure doubleSidedArrows is an array
  if (!Array.isArray(doubleSidedArrows)) {
    console.warn("handleDoubleSidedArrowDragEnd: doubleSidedArrows is not an array", doubleSidedArrows);
    return;
  }

  // Check if this is a restoration by looking at the arrow data
  const arrow = currentArrow || doubleSidedArrows.find((a) => a.id === id);
  const isRestoration = arrow?.isRestored || arrow?.isFromHistory;

  if (isRestoration) {
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date doubleSidedArrows array
  setDoubleSidedArrows((prevArrows) => {
    // CRITICAL: Find the current arrow to preserve all properties
    // If currentArrow is provided, use it; otherwise find it from the current state
    // This ensures we preserve rotation, scaleX, scaleY, and all other properties
    const existingArrow = currentArrow || prevArrows.find((arrow) => arrow.id === id);
    
    if (!existingArrow) {
      console.warn("handleDoubleSidedArrowDragEnd: arrow not found", id);
      return prevArrows; // Return unchanged if arrow not found
    }

    // CRITICAL: Preserve ALL properties from the existing arrow (rotation, scaleX, scaleY, etc.)
    // Only update x, y - everything else should be preserved
    const updatedArrows = prevArrows.map((arrow) =>
      arrow.id === id
        ? {
            ...existingArrow, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
            x,
            y,
          }
        : arrow
    );
    
    // Debug logging to verify rotation is preserved
    const updatedArrow = updatedArrows.find((arrow) => arrow.id === id);
    if (updatedArrow) {
    }

    // Save to history with the updated arrows
    // CRITICAL: Call saveToHistory OUTSIDE the setDoubleSidedArrows callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedArrows;
      }
      
      // CRITICAL: Call saveToHistory asynchronously to ensure it's not batched with the setDoubleSidedArrows update
      // This ensures the history entry is created even if React batches the state updates
      setTimeout(() => {
        // Pass the updated arrows directly to saveToHistory using the override parameter
        // This ensures the history entry has the correct state even though React state might not have updated yet
        saveToHistory(
          "double_sided_arrow_drag_end",
          null, // currentImagesOverride
          null, // currentArrowsOverride
          updatedArrows, // currentDoubleSidedArrowsOverride
          null, // currentLinesOverride
          null, // currentTextsOverride
          null, // currentSelectedTextOverride
          null, // currentSelectedTextIdOverride
          null, // currentRectanglesOverride
          null, // currentOffCanvasRectanglesOverride
          null, // currentCirclesOverride
          null // currentOffCanvasCirclesOverride
        );
      }, 0);
    }

    return updatedArrows;
  });
};

/**
 * Create a function to handle double-sided arrow drag end with bound dependencies
 * @param {Array} doubleSidedArrows - Current double-sided arrows array
 * @param {Function} setDoubleSidedArrows - Function to set double-sided arrows
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle double-sided arrow drag end
 */
// Track last drag end call to prevent duplicates
let lastDoubleSidedArrowDragEndCall = null;
const DOUBLE_SIDED_ARROW_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleDoubleSidedArrowDragEnd = (
  doubleSidedArrows,
  setDoubleSidedArrows,
  saveToHistory,
  isUndoingRedoing
) => {
  return (id, x, y, currentArrow = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleDoubleSidedArrowDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;
    
    if (
      lastDoubleSidedArrowDragEndCall &&
      lastDoubleSidedArrowDragEndCall.key === callKey &&
      now - lastDoubleSidedArrowDragEndCall.timestamp < DOUBLE_SIDED_ARROW_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }
    
    // Update last call tracking
    lastDoubleSidedArrowDragEndCall = {
      key: callKey,
      timestamp: now,
    };
    
    return handleDoubleSidedArrowDragEnd(
      id,
      x,
      y,
      doubleSidedArrows,
      setDoubleSidedArrows,
      saveToHistory,
      isUndoingRedoing,
      currentArrow // Pass the current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
    );
  };
};

